import { useState, useRef, useEffect } from 'react'
import { Plus } from 'lucide-react'
import { clsx } from 'clsx'
import { useAuthStore } from '@/stores/authStore'

const QUICK_EMOJIS = ['👍', '❤️', '🎉', '👏', '🔥']

interface ReactionBarProps {
  reactions: Record<string, string[]>
  onAdd: (emoji: string) => void
  onRemove: (emoji: string) => void
}

export function ReactionBar({ reactions, onAdd, onRemove }: ReactionBarProps) {
  const uid = useAuthStore((s) => s.user?.uid)
  const [showPicker, setShowPicker] = useState(false)
  const pickerRef = useRef<HTMLDivElement>(null)

  // 클릭 외부 닫기
  useEffect(() => {
    if (!showPicker) return
    const handleClickOutside = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setShowPicker(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showPicker])

  const [processing, setProcessing] = useState(false)

  const handleClick = async (emoji: string) => {
    if (!uid || processing) return
    setProcessing(true)
    try {
      const users = reactions[emoji] || []
      if (users.includes(uid)) {
        onRemove(emoji)
      } else {
        onAdd(emoji)
      }
    } finally {
      // 짧은 딜레이 후 해제 (Firestore 반영 대기)
      setTimeout(() => setProcessing(false), 300)
    }
  }

  const activeEmojis = Object.entries(reactions).filter(([, users]) => users.length > 0)

  return (
    <div className="flex items-center gap-1 flex-wrap">
      {activeEmojis.map(([emoji, users]) => (
        <button
          key={emoji}
          onClick={(e) => { e.stopPropagation(); handleClick(emoji) }}
          className={clsx(
            'flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border transition-colors',
            uid && users.includes(uid)
              ? 'border-primary-300 bg-primary-50 dark:bg-primary-900/20 dark:border-primary-700'
              : 'border-base hover:border-primary-200 dark:hover:border-primary-800'
          )}
        >
          <span>{emoji}</span>
          <span className="text-caption font-medium">{users.length}</span>
        </button>
      ))}
      <div className="relative" ref={pickerRef}>
        <button
          onClick={(e) => { e.stopPropagation(); setShowPicker(!showPicker) }}
          className="flex items-center justify-center w-6 h-6 rounded-full border border-dashed border-base text-caption hover:border-primary-300 hover:text-primary-500 transition-colors"
        >
          <Plus className="w-3 h-3" />
        </button>
        {showPicker && (
          <div className="absolute bottom-full left-0 mb-1 flex items-center gap-1 p-1.5 bg-surface-primary border border-base rounded-lg shadow-lg z-20">
            {QUICK_EMOJIS.map((emoji) => (
              <button
                key={emoji}
                onClick={(e) => { e.stopPropagation(); handleClick(emoji); setShowPicker(false) }}
                className="w-7 h-7 flex items-center justify-center rounded hover:bg-surface-tertiary transition-colors text-base"
              >
                {emoji}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
